'use client';

import { ReactNode, useCallback, useMemo } from 'react';

import { IntlError, NextIntlClientProvider } from 'next-intl';

interface I_NextIntlProviderProps {
  locale: string;
  messages: Record<string, unknown>;
  children: ReactNode;
}

export function NextIntlProvider({
  locale,
  messages,
  children
}: I_NextIntlProviderProps) {
  const timeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  );

  const onError = useCallback(
    (err: IntlError) => {
      if (process.env.NODE_ENV !== 'production') {
        const key = 'key' in err ? (err.key as string) : undefined;
        const namespace =
          'namespace' in err ? (err.namespace as string) : undefined;

        // Resolve message inline to avoid depending on `messages` ref
        const messageValue =
          key !== undefined
            ? (namespace ? `${namespace}.${key}` : key)
                .split('.')
                .reduce<unknown>(
                  (obj, part) => (obj as Record<string, unknown>)?.[part],
                  messages
                )
            : undefined;

        console.error('next-intl error:', {
          code: err.code,
          key,
          namespace,
          messageValue,
          messageType: typeof messageValue,
          isArray: Array.isArray(messageValue)
        });
      } else {
        console.error('next-intl error:', err);
      }
    },
    [messages]
  );

  const getMessageFallback = useCallback(
    ({ namespace, key }: { namespace?: string; key: string }) => {
      return `[missing ${namespace ? namespace + '.' : ''}${key}]`;
    },
    []
  );

  return (
    <NextIntlClientProvider
      locale={locale}
      messages={messages}
      timeZone={timeZone}
      onError={onError}
      getMessageFallback={getMessageFallback}
    >
      {children}
    </NextIntlClientProvider>
  );
}
